import React from 'react';
import {useLegacyTelematicsDeviceContext} from 'pages/Unitlookup/stores/LegacyTelematicsDevice/LegacyTelematicsDeviceProvider';
import GenericConfigButton from '../GenericConfiguration/GenericConfigButton';
import {AnalyticsEvents} from '../../../../components/analytics/analyticsEvents';
import {
  ConfigurationTemplate,
  ConfigurationVersion,
  TelematicsDeviceConfigurationV2Api
} from '../../stores/TelematicsDeviceConfiguration/TelematicsDeviceConfigurationV2Api';
import {AuthState} from "@okta/okta-auth-js";

interface Props {
  authState: AuthState | null;
  configuration: ConfigurationEnum;
  label?: string;
  parameters?: Record<string, any>;
  validateParameters?: boolean;
}

export enum ConfigurationEnum {
  SKYJACK_BMS_ENABLED = 'setup-skyjack-bms-enabled',
  SKYJACK_BMS_DISABLED = 'setup-skyjack-bms-disabled',
  WN_COMPACTION_MODE = 'setup-wn-compaction-mode'
}

const Configuration: React.FC<Props> = props => {
  const telematicsDevice = useLegacyTelematicsDeviceContext().telematicsDevice;
  const [configurationTemplate, setConfigurationTemplate] = React.useState<ConfigurationTemplate | null>(null);
  const [actionDisabled, setActionDisabled] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (telematicsDevice) {
      TelematicsDeviceConfigurationV2Api.getDeviceConfigurationTemplates(
        props.authState?.accessToken,
        telematicsDevice.serialNumber
      ).then((configurationTemplates: ConfigurationTemplate[]) => {
        if( configurationTemplates) {
          const templates = configurationTemplates.filter(configurationTemplate => configurationTemplate.href.includes(props.configuration));
          if (templates.length === 1) {
            setConfigurationTemplate(templates[0]);
            const currentVersion = templates[0].versions.find(v => v.versionType === 'CURRENT');
            if (currentVersion) {
              validateParameters(currentVersion, props.parameters);
            }
          }
        }
      });
    } else {
      setConfigurationTemplate(null);
    }
  }, [telematicsDevice]);

  const validateParameters = (version: ConfigurationVersion, parameters?: Record<string, any>) => {
    if (!parameters || !props.validateParameters) {
      // No parameters provided or skip validation
      setActionDisabled(false);
      return;
    }

    // Check if all provided parameters are valid according to the allowed values
    let isValid = true;
    
    for (const param of version.parameters) {
      const paramValue = parameters[param.name];
      
      // If parameter is required (has allowedValues) but not provided, mark as invalid
      if (param.allowedValues.length > 0 && paramValue === undefined) {
        console.warn(`Required parameter ${param.name} is missing`);
        isValid = false;
        break;
      }
      
      // If parameter is provided but not in allowedValues, mark as invalid
      if (paramValue !== undefined && param.allowedValues.length > 0 && !param.allowedValues.includes(String(paramValue))) {
        console.warn(`Parameter ${param.name} with value ${paramValue} is not in allowed values: ${param.allowedValues.join(', ')}`);
        isValid = false;
        break;
      }
    }
    
    setActionDisabled(!isValid);
  };

  const executeConfiguration = () => {
    if (!!configurationTemplate && !!telematicsDevice) {
      const configuration$ = TelematicsDeviceConfigurationV2Api.pushConfigurationToDevice(
        props.authState?.accessToken, 
        configurationTemplate, 
        telematicsDevice.serialNumber,
        props.parameters
      );

      return Promise.all([configuration$])
          .then(() => Promise.resolve())
          .catch(() => Promise.reject(new Error(`${getLabel()} configuration failed.`)));
    }
    return Promise.reject(new Error('Missing required data to perform device setup.'));
  };

  const getLabel = () => {
    return props.label ?? configurationTemplate?.name ?? "";
  };

  const confirmLabel = `Are you sure you want to execute configuration: ${getLabel()}?`;

  return (
    <GenericConfigButton
      key={configurationTemplate?.href}
      visible={!!configurationTemplate}
      disabled={actionDisabled}
      buttonLabel={getLabel()}
      confirmLabel={confirmLabel}
      analyticsEvent={AnalyticsEvents.Configuration}
      performConfiguration={() => executeConfiguration()}
    />
  );
};

export default Configuration;
